import { PetType } from 'constants/pet-type';
import {
  AgeSegmentationIds,
  AllAllergySegmentationIds,
  BreedSegmentationIds,
  CatNutritionSegmentationIds,
  NutritionSegmentationIds,
  ProteinTagValues,
} from 'constants/segmentation';
import { InputProductType, WetFoodTagValues } from 'interfaces/api-input/input-product-type';
import { ActivityLevel, BodyCondition } from 'types/pet-profile';
import { axios } from 'utils/axios';

/** Base interface for common pet properties */
export interface BasePet {
  name: string | null;
  pet_type: (typeof PetType)[keyof typeof PetType];
  /** Sending null to the server will remove the value */
  age?: AgeSegmentationIds | null;
  needs?: Array<NutritionSegmentationIds | CatNutritionSegmentationIds>;
  /** Sending null to the server will remove the value */
  breed?: BreedSegmentationIds | null;
  allergies?: Array<AllAllergySegmentationIds>;
  food_types?: Array<InputProductType>;
  weight?: number;
  date_of_birth?: string;
  body_condition?: BodyCondition;
  neutered?: boolean;
  activity_level?: ActivityLevel;
  has_special_needs?: boolean;
  kcal_updated_at?: string;
  daily_kcal?: number;
}

/** Cat-specific properties */
interface CatSpecificProps {
  wet_food_tags?: Array<WetFoodTagValues>;
  protein_tags?: Array<ProteinTagValues>;
  disliked_protein_tags?: Array<ProteinTagValues>;
}

export interface DogPet extends BasePet {
  id: string;
  updated_at: string;
  pet_type: typeof PetType.Dog;
}

export interface CatPet extends BasePet, CatSpecificProps {
  id: string;
  updated_at: string;
  pet_type: typeof PetType.Cat;
}

export type Pet = DogPet | CatPet;

/** Properties required when updating a pet */
export interface UpdatePetPayload extends BasePet {
  id?: string;
  customer_id?: string;
  wet_food_tags?: CatSpecificProps['wet_food_tags'];
  protein_tags?: CatSpecificProps['protein_tags'];
  disliked_protein_tags?: CatSpecificProps['disliked_protein_tags'];
}

/** Properties required when creating a new pet */
export type CreatePetPayload = (
  Omit<DogPet, 'id' | 'updated_at'> | Omit<CatPet, 'id' | 'updated_at'>
) & { customer_id: string; id?: never };

export type CalorieCalculationPayload = Pick<
  NonNullable<Required<BasePet>>,
  'pet_type' | 'activity_level' | 'body_condition' | 'neutered' | 'date_of_birth' | 'weight'
>;

/**
 * Fetches all pets for a given customer
 */
export const getPets = (customerId: number): Promise<Pet[]> =>
  axios
    .get(`/api/legacy/pets?customer_id=${customerId}`, {
      isPdexApi: true,
    })
    .then(({ data }) => data);

/**
 * Fetches a single pet by ID
 */
export const getPet = (petId: string): Promise<Pet> =>
  axios
    .get(`/api/legacy/pets/${petId}`, {
      isPdexApi: true,
    })
    .then(({ data }) => data);

type KeysOfUnion<T> = T extends T ? keyof T : never;
type NoExtraKeys<T, Shape> = T & Record<Exclude<keyof T, KeysOfUnion<Shape>>, never>;
/**
 * Creates a new pet
 */
export const createPet = <T extends CreatePetPayload>(
  pet: NoExtraKeys<T, CreatePetPayload>
): Promise<Pet> =>
  axios
    .post(
      '/api/legacy/pets',
      { pet },
      {
        isPdexApi: true,
      }
    )
    .then(({ data }) => data);

/**
 * Updates an existing pet
 */
export const updatePet = (pet: UpdatePetPayload): Promise<Pet> =>
  axios.put(`/api/legacy/pets/${pet.id}`, { pet }, { isPdexApi: true }).then(({ data }) => data);

/**
 * Deletes a pet by ID
 */
export const deletePet = (petId: string): Promise<void> =>
  axios.delete(`/api/legacy/pets/${petId}`, {
    isPdexApi: true,
  });

export const calculatePetKcal = (
  payload: CalorieCalculationPayload
): Promise<{
  age: NonNullable<Pet['age']>;
  daily_kcal: NonNullable<Pet['daily_kcal']>;
}> =>
  axios
    .post(`/api/legacy/pets/calorie-calculation`, payload, { isPdexApi: true })
    .then(({ data }) => data);
